import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { RenderJob, RenderNotImplementedError, RenderPackFile, RenderPackSizes } from '../render-flow.model';
import { RenderFlowService } from '../render-flow.service';
import { RenderPackService } from '../render-pack.service';
import {
  RENDER_PACK_KEYS,
  RENDER_PACK_PRESETS,
  RenderOrientation,
  RenderPackKey,
  renderPackBytes,
  renderPackFileName,
  renderPackKey,
  renderPackSize,
  renderPackEncode,
  renderPackSpec,
  renderPackTotalBytes,
  renderFileStem,
  renderVideoFileName,
} from '../render.data';

// A row of "Optimize for platforms": portrait first, as in her table.
interface PackRow {
  id: string;
  label: string;
  cells: PackCell[];
}

interface PackCell {
  key: RenderPackKey;
  orientation: RenderOrientation;
  // Empty while the backend's sizes are on their way, or when it could not say.
  size: string;
  // The file's spec, shown on hover; says so when the landscape sits in a vertical frame. None for
  // the backend's pack: its files are the renderer's, not the encodes these specs describe.
  tip: string | null;
  verticalFrame: boolean;
  ariaLabel: string;
}

// "Optimize for platforms" (Figma 28563:27840, 2026-09-24) — the one way to download: the videos as
// each platform wants them, both orientations, in one ZIP; "File name" (Figma 28701:45139) names
// them, "Save configuration" keeps the choice. The downloads page's card, and the library's
// download button opens it in a dialog (designer, 2026-10-16). Put on the card's own element
// (`<aside class="order-card pack-card" app-render-pack-card>`), so the page's card styles hold.
// The dev render backend packs one ZIP (render_download) and weighs each cell's file
// (render_download_info); the mock, and the samples, save the source videos in the ZIP's place, one
// per click (RenderPackService.saveFile): the button then offers the next one.
@Component({
  // An attribute: the card's flex layout needs the card's own element as the host.
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-render-pack-card]',
  templateUrl: './render-pack-card.component.html',
})
export class RenderPackCardComponent implements OnChanges {
  @Input() public job?: RenderJob;

  public packRows: ReadonlyArray<PackRow> = [];
  public packAllSize = '';
  // The source videos, one per click, in the ZIP's place.
  public standIn = true;
  public selected: Set<RenderPackKey>;
  public remember: boolean;
  public packing = false;
  public packError = '';
  // The pack's files still to save, the next one first, and what the last click saved.
  public queue: RenderPackFile[] = [];
  public saved = '';
  // "File name": its field open, and what is typed in it.
  public renaming = false;
  public nameDraft = '';

  // What the backend says each cell's file weighs, and the model it said it for.
  private sizes: RenderPackSizes | null = null;
  private sizesFor = '';

  @ViewChild('editButton') private editButton?: ElementRef<HTMLButtonElement>;

  // The field takes the focus, its text selected, as it opens.
  @ViewChild('nameInput') private set nameInput(input: ElementRef<HTMLInputElement> | undefined) {
    if (input) {
      setTimeout(() => {
        input.nativeElement.focus();
        input.nativeElement.select();
      });
    }
  }

  constructor(private readonly flow: RenderFlowService, private readonly pack: RenderPackService) {
    this.selected = new Set(pack.choice);
    this.remember = pack.settings.remember;
    this.fillTable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['job'];
    if (change && change.previousValue?.modelId !== change.currentValue?.modelId) {
      this.resetQueue();
    }
    const standIn = this.job ? this.pack.standIn(this.job) : true;
    if (standIn !== this.standIn || (!standIn && this.job?.modelId !== this.sizesFor)) {
      this.standIn = standIn;
      this.sizes = null;
      this.sizesFor = '';
      this.fillTable();
      if (this.job && !standIn) {
        this.loadSizes(this.job);
      }
    }
  }

  // --- the table ---

  public get allSelected(): boolean {
    return this.selected.size === RENDER_PACK_KEYS.length;
  }

  public get someSelected(): boolean {
    return this.selected.size > 0 && !this.allSelected;
  }

  public isSelected(key: RenderPackKey): boolean {
    return this.selected.has(key);
  }

  public toggle(key: RenderPackKey): void {
    const next = new Set(this.selected);
    if (!next.delete(key)) {
      next.add(key);
    }
    this.choose(next);
  }

  public toggleAll(): void {
    this.choose(new Set(this.allSelected ? [] : RENDER_PACK_KEYS));
  }

  public toggleRemember(): void {
    this.remember = !this.remember;
    this.pack.save(this.orderedSelection, this.remember);
  }

  public trackByRow(index: number, row: PackRow): string {
    return row.id;
  }

  // The table's sizes: the backend's pack holds a file per ticked cell, so a cell weighs its file and
  // the whole pack every cell's; the stand-in's are the encodes' estimates.
  private fillTable(): void {
    const live = !this.standIn;
    const sizes = this.sizes;
    this.packRows = RENDER_PACK_PRESETS.map((preset) => ({
      id: preset.id,
      label: preset.label,
      cells: (['portrait', 'landscape'] as const).map((orientation) => {
        const key = renderPackKey(preset.id, orientation);
        const encode = preset[orientation];
        const frame = encode.verticalFrame && !live ? 'Landscape in a vertical 9:16 frame for phone feeds' : '';
        const bytes = live ? sizes?.[key] : renderPackBytes(encode);
        return {
          key,
          orientation,
          size: bytes ? renderPackSize(bytes) : '',
          tip: live ? null : frame ? `${frame}\n${renderPackSpec(encode)}` : renderPackSpec(encode),
          verticalFrame: !!encode.verticalFrame,
          ariaLabel: `${preset.label}, ${frame ? frame.charAt(0).toLowerCase() + frame.slice(1) : orientation}`,
        };
      }),
    }));
    const allBytes = live
      ? sizes && RENDER_PACK_KEYS.reduce((total, key) => total + sizes[key], 0)
      : renderPackTotalBytes(RENDER_PACK_KEYS);
    this.packAllSize = allBytes ? renderPackSize(allBytes) : '';
  }

  // A model's sizes once; a failure leaves the cells without one (the download says what is wrong).
  private loadSizes(job: RenderJob): void {
    this.sizesFor = job.modelId;
    this.pack.sizes(job).then(
      (sizes) => {
        if (this.sizesFor === job.modelId) {
          this.sizes = sizes;
          this.fillTable();
        }
      },
      (error) => console.warn('Render flow: render_download_info failed', error),
    );
  }

  // --- the download ---

  public get ctaLabel(): string {
    if (this.packing) {
      return 'Preparing the pack…';
    }
    return this.queue.length ? `Download the ${this.queue[0].label}` : 'Download ZIP pack';
  }

  // The first click fetches the pack and saves its first file; while files are left, each click
  // saves the next.
  public async onDownloadPack(): Promise<void> {
    if (!this.job || this.packing || !this.selected.size) {
      return;
    }
    if (this.queue.length) {
      this.saveNext();
      return;
    }
    this.packing = true;
    this.packError = '';
    this.saved = '';
    try {
      this.queue = [...(await this.pack.files(this.job, this.orderedSelection)).files];
      this.saveNext();
    } catch (e) {
      this.packError = e instanceof RenderNotImplementedError ? e.message : 'The download was interrupted. Try again — there is no second charge.';
    } finally {
      this.packing = false;
    }
  }

  private saveNext(): void {
    const file = this.queue.shift();
    if (!file) {
      return;
    }
    this.pack.saveFile(file);
    this.saved = this.queue.length
      ? `Saved the ${file.label}. Your browser takes one file per click: the ${this.queue[0].label} is next.`
      : `Saved the ${file.label}.`;
  }

  private resetQueue(): void {
    this.queue = [];
    this.saved = '';
  }

  // --- File name ---

  // What the pack saves for the ticked cells: the backend's ZIP a file per cell, named for its
  // platform; the stand-in the source video of each orientation they touch (RenderBackend.getPack).
  // Each on one line: a long name gives way to an ellipsis, " landscape.mp4" stays whole — it tells
  // them apart (designer, 2026-10-23).
  public get packFileNames(): Array<{ name: string; stem: string; tail: string }> {
    const job = this.job;
    if (!job) {
      return [];
    }
    const stem = renderFileStem(job.name) || 'video';
    const names = this.standIn
      ? (['landscape', 'portrait'] as const)
        .filter((orientation) => [...this.selected].some((key) => key.endsWith(`.${orientation}`) && renderPackEncode(key)))
        .map((orientation) => renderVideoFileName(job.name, orientation))
      : this.orderedSelection.map((key) => renderPackFileName(job.name, key));
    return names.map((name) => ({ name, stem, tail: name.slice(stem.length) }));
  }

  public startRename(): void {
    if (this.job) {
      this.nameDraft = this.job.name;
      this.renaming = true;
    }
  }

  // Enter, or the field left. An empty name, or one with nothing usable in a file name, keeps the
  // old one. From the keyboard the focus goes back to the pencil.
  public commitRename(fromKeyboard = false): void {
    if (!this.renaming || !this.job) {
      return;
    }
    this.renaming = false;
    const name = renderFileStem(this.nameDraft);
    if (name && name !== this.job.name) {
      this.flow.renameJob(this.job.modelId, name);
      this.resetQueue();
    }
    if (fromKeyboard) {
      this.focusRename();
    }
  }

  public cancelRename(): void {
    this.renaming = false;
    this.focusRename();
  }

  private focusRename(): void {
    setTimeout(() => this.editButton?.nativeElement.focus());
  }

  private choose(next: Set<RenderPackKey>): void {
    this.selected = next;
    this.packError = '';
    this.resetQueue();
    if (this.remember) {
      this.pack.save(this.orderedSelection, true);
    }
  }

  // In the table's order, whatever order they were ticked in.
  private get orderedSelection(): RenderPackKey[] {
    return RENDER_PACK_KEYS.filter((key) => this.selected.has(key));
  }
}
